import React, { useState, useRef } from 'react';
import TableOmniNew from 'allGlobalComponents/TableOmniNew';
import { Tooltip, Typography, Box, FormControl, InputLabel, Select, MenuItem, Badge } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { copyContent } from 'app/utility';
import { useTheme } from '@material-ui/core/styles';
import moment from 'moment';
import ButtonOmni from 'allGlobalComponents/ButtonOmni';
import FilterListIcon from '@mui/icons-material/FilterList';
import GetAppIcon from '@mui/icons-material/GetApp';
import ModalOmni from 'allGlobalComponents/ModalOmni';
import FormFieldOmni from 'allGlobalComponents/FormFieldOmni';
import CoreHttpHandler from 'http/services/CoreHttpHandler';
import { CSVLink } from 'react-csv';

const SORT_OPTIONS = [
    { label: 'Ascending', value: 'ASC' },
    { label: 'Descending', value: 'DESC' }
];

const ORDER_BY_OPTIONS = [
    { label: 'Date', value: 'dt' },
    { label: 'Id', value: 'id' },
    { label: 'Client Id', value: 'client_id' }
];

const useStyles = makeStyles((theme) => ({
    tableSection: {
        marginTop: theme.spacing(3),
        padding: theme.spacing(3),
        background: theme.palette.type === 'dark'
            ? 'linear-gradient(135deg, #1e3a5f 0%, #1e293b 100%)'
            : 'linear-gradient(135deg, #e3f2fd 0%, #f5f5f5 100%)',
        borderRadius: 16,
        border: `1px solid ${theme.palette.type === 'dark' ? '#334155' : '#e0e0e0'}`,
        boxShadow: theme.palette.type === 'dark'
            ? '0 8px 32px rgba(0, 0, 0, 0.3)'
            : '0 4px 20px rgba(0, 0, 0, 0.1)',
    },
    tableTitle: {
        fontSize: '2rem',
        fontWeight: 600,
        marginBottom: theme.spacing(3),
        color: theme.palette.text.primary,
    },
    headerSection: {
        display: 'flex',
        alignItems: "center",
        justifyContent: 'space-between',
        marginBottom: theme.spacing(3),
    },
    buttonGroup: {
        display: 'flex',
        gap: theme.spacing(1.5),
    },
    filterContainer: {
        padding: theme.spacing(3),
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2.5)
    },
    buttonContainer: {
        width: "100%",
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: theme.spacing(2),
        gap: theme.spacing(2)
    },
    buttonGroupModal: {
        display: 'flex',
        gap: theme.spacing(2)
    },
    sectionTitle: {
        fontSize: '14px',
        fontWeight: 600,
        marginBottom: theme.spacing(1),
        color: theme.palette.text.primary
    }
}));

function TemplateReportsTable({
    loading,
    data,
    currentParams,
    setCurrentParams,
    totalItems,
    tableFilters,
    onTableFiltersChange,
    dateRange,
    clientId
}) {
    const theme = useTheme();
    const classes = useStyles();
    const csvLink = useRef();
    const [copied, setCopied] = useState(false);
    const [filterDialogOpen, setFilterDialogOpen] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [dataforExport, setDataforExport] = useState([]);
    const [flowModalOpen, setFlowModalOpen] = useState(false);
    const [selectedFlowData, setSelectedFlowData] = useState(null);

    const handleViewFlow = (flowData) => {
        setSelectedFlowData(flowData);
        setFlowModalOpen(true);
    };

    const [tempFilters, setTempFilters] = useState({
        sortBy: tableFilters.sortBy,
        orderBy: tableFilters.orderBy,
        user_reply: tableFilters.user_reply || '',
        customer_number: tableFilters.customer_number || ''
    });

    // Calculate active filter count
    const getActiveFilterCount = () => {
        let count = 0;
        if (tableFilters.sortBy !== 'ASC') count++;
        if (tableFilters.orderBy !== 'dt') count++;
        if (tableFilters.user_reply) count++;
        if (tableFilters.customer_number) count++;
        return count;
    };

    const calculatedFilterCount = getActiveFilterCount();

    // Handle filter changes in dialog
    const handleFilterChange = (filterName, value) => {
        setTempFilters(prev => ({
            ...prev,
            [filterName]: value
        }));
    };

    // Apply filters
    const handleApplyFilters = () => {
        const filtersToApply = { ...tempFilters };
        if (!filtersToApply.user_reply) delete filtersToApply.user_reply;
        if (!filtersToApply.customer_number) delete filtersToApply.customer_number;

        onTableFiltersChange(filtersToApply);
        setFilterDialogOpen(false);
    };

    // Clear filters
    const handleClearFilters = () => {
        const defaultFilters = {
            sortBy: 'ASC',
            orderBy: 'dt',
            user_reply: '',
            customer_number: ''
        };
        setTempFilters(defaultFilters);
        onTableFiltersChange(defaultFilters);
        setFilterDialogOpen(false);
    };

    // Cancel filter changes
    const handleCancelFilters = () => {
        setTempFilters({
            sortBy: tableFilters.sortBy,
            orderBy: tableFilters.orderBy,
            user_reply: tableFilters.user_reply || '',
            customer_number: tableFilters.customer_number || ''
        });
        setFilterDialogOpen(false);
    };

    // Open filter dialog
    const handleOpenFilterDialog = () => {
        setTempFilters({
            sortBy: tableFilters.sortBy,
            orderBy: tableFilters.orderBy,
            user_reply: tableFilters.user_reply || '',
            customer_number: tableFilters.customer_number || ''
        });
        setFilterDialogOpen(true);
    };

    // Generate filename for export
    const generateFileName = () => {
        const today = moment().format('YYYY-MM-DD');
        let fileName = 'template_reports';

        const isDateFiltered =
            !dateRange?.startDate?.isSame(moment.utc().startOf('day'), 'day') ||
            !dateRange?.endDate?.isSame(moment.utc().endOf('day'), 'day');

        const isFiltered =
            tableFilters.sortBy !== 'ASC' ||
            tableFilters.orderBy !== 'dt' ||
            tableFilters.user_reply ||
            tableFilters.customer_number;

        if (isDateFiltered || isFiltered) {
            fileName += '_filtered';

            if (isDateFiltered) {
                const startDateStr = dateRange?.startDate?.format('YYYY-MM-DD');
                const endDateStr = dateRange?.endDate?.format('YYYY-MM-DD');

                if (startDateStr === endDateStr) {
                    fileName += `_${startDateStr}`;
                } else {
                    fileName += `_${startDateStr}_to_${endDateStr}`;
                }
            }

            if (tableFilters.user_reply) {
                fileName += `_reply_${tableFilters.user_reply.substring(0, 10)}`;
            }
            if (tableFilters.customer_number) {
                fileName += `_customer_${tableFilters.customer_number}`;
            }
        } else {
            fileName += '_full_export';
        }

        fileName += `_${today}.csv`;
        return fileName;
    };

    // Format data for export
    const formatDataForExport = (rawData) => {
        if (!rawData || rawData.length === 0) return [];

        return rawData.map((item, index) => ({
            'S.No': index + 1,
            'ID': item.id || '-',
            'Client ID': item.client_id || '-',
            'Message ID': item.message_id || '-',
            'Customer Number': item.customer_number || '-',
            'Template Name': item.template_name || '-',
            'User Reply': item.is_flow_response && item.flow_data
                ? item.flow_data.screens?.map(s => s.responses?.map(r => `${r.question}: ${r.answer}`).join('; ')).join(' | ')
                : (item.user_reply || '-'),
            'Sent Time': item.sent_time ? moment(item.sent_time).format('MMM Do YYYY, h:mm A') : '-',
            'Response Time': item.response_time ? moment(item.response_time).format('MMM Do YYYY, h:mm A') : '-'
        }));
    };

    // Fetch all data for export
    const fetchAllDataForExport = () => {
        return new Promise((resolve, reject) => {
            const payload = {
                sortby: tableFilters.sortBy,
                orderby: tableFilters.orderBy,
                limit: 999999,
                page: 0
            };

            if (clientId) {
                payload.clientId = clientId;
            }

            if (dateRange.startDate && dateRange.endDate) {
                payload.startDate = dateRange.startDate;
                payload.endDate = dateRange.endDate;
            }

            if (tableFilters.user_reply) {
                payload.user_reply = tableFilters.user_reply;
            }
            if (tableFilters.customer_number) {
                payload.customer_number = tableFilters.customer_number;
            }

            CoreHttpHandler.request(
                'analytics',
                'webhookReports',
                payload,
                (response) => {
                    const res = response.data?.data?.data || [];
                    resolve(res);
                },
                (error) => {
                    console.error('Export fetch error:', error);
                    reject(error);
                }
            );
        });
    };

    // Handle export
    const handleExport = async () => {
        try {
            setIsExporting(true);

            const exportData = await fetchAllDataForExport();

            if (exportData.length === 0) {
                alert('No data available for export with current filters.');
                setIsExporting(false);
                return;
            }

            const formattedData = formatDataForExport(exportData);
            setDataforExport(formattedData);

            setTimeout(() => {
                if (csvLink.current && csvLink.current.link) {
                    csvLink.current.link.click();
                }
                setIsExporting(false);
            }, 1000);
        } catch (error) {
            console.error('Export failed:', error);
            alert('Export failed. Please try again.');
            setIsExporting(false);
        }
    };

    const headCells = [
        {
            id: 'id',
            numeric: true,
            disablePadding: false,
            label: 'ID',
            customRow: row => (
                <Tooltip title={copied ? 'Copied' : 'Copy'} arrow>
                    <button onClick={() => copyContent(row.id, setCopied)}>{row.id}</button>
                </Tooltip>
            )
        },
        {
            id: 'client_id',
            numeric: true,
            disablePadding: false,
            label: 'Client Id',
            customRow: row => (
                <Tooltip title={copied ? 'Copied' : 'Copy'} arrow>
                    <button onClick={() => copyContent(row.client_id, setCopied)}>{row.client_id}</button>
                </Tooltip>
            )
        },
        {
            id: 'message_id',
            numeric: false,
            disablePadding: false,
            label: 'Message Id',
            customRow: row => (
                <Tooltip title={copied ? 'Copied' : 'Copy'} arrow>
                    <button onClick={() => copyContent(row.message_id, setCopied)}>
                        {row.message_id ? row.message_id.substring(0, 20) + '...' : '-'}
                    </button>
                </Tooltip>
            )
        },
        {
            id: 'customer_number',
            numeric: false,
            disablePadding: false,
            label: 'Customer Number',
            customRow: row => (
                <Tooltip title={copied ? 'Copied' : 'Copy'} arrow>
                    <button onClick={() => copyContent(row.customer_number, setCopied)}>
                        {row.customer_number || '-'}
                    </button>
                </Tooltip>
            )
        },
        {
            id: 'template_name',
            numeric: false,
            disablePadding: false,
            label: 'Template Name',
            customRow: row => <span>{row.template_name || '-'}</span>
        },
        {
            id: 'user_reply',
            numeric: false,
            disablePadding: false,
            label: 'User Reply',
            customRow: row => {
                if (row.is_flow_response && row.flow_data) {
                    return (
                        <span
                            onClick={(e) => {
                                e.stopPropagation();
                                handleViewFlow(row.flow_data);
                            }}
                            style={{
                                color: theme.palette.text.primary,
                                cursor: 'pointer',
                                fontWeight: 500,
                                textDecoration: 'underline'
                            }}
                        >
                            View Flow
                        </span>
                    );
                }
                return <span>{row.user_reply || '-'}</span>;
            }
        },
        {
            id: 'sent_time',
            numeric: false,
            disablePadding: false,
            label: 'Sent Time',
            customRow: row => (
                <span>
                    {row.sent_time ? moment(row.sent_time).format('MMM Do YYYY, h:mm A') : '-'}
                </span>
            )
        },
        {
            id: 'response_time',
            numeric: false,
            disablePadding: false,
            label: 'Response Time',
            customRow: row => (
                <span>
                    {row.response_time ? moment(row.response_time).format('MMM Do YYYY, h:mm A') : '-'}
                </span>
            )
        }
    ];

    // Flow modal content
    const flowModalContent = selectedFlowData ? (
        <div style={{ padding: theme.spacing(3) }}>
            <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
                <Typography style={{ fontSize: '14px', fontWeight: 600, color: theme.palette.text.secondary }}>
                    Total Screens: {selectedFlowData.total_screens || 0}
                </Typography>
            </Box>
            {selectedFlowData.screens && selectedFlowData.screens.map((screen, screenIndex) => (
                <Box
                    key={screen.screen_id || screenIndex}
                    mb={2}
                    p={2}
                    style={{
                        background: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                        borderRadius: '12px',
                        border: `1px solid ${theme.palette.divider}`
                    }}
                >
                    <Typography style={{ fontSize: '13px', fontWeight: 600, color: theme.palette.text.secondary, marginBottom: 8 }}>
                        {screen.screen_id?.replace(/_/g, ' ').replace(/\d+/g, (n) => String(Number(n) + 1)).toUpperCase() || `Screen ${screenIndex + 1}`}
                    </Typography>
                    {screen.responses && screen.responses.map((resp, respIndex) => (
                        <Box key={respIndex} mb={respIndex < screen.responses.length - 1 ? 1.5 : 0}>
                            <Typography style={{ fontSize: '12px', color: theme.palette.text.secondary, marginBottom: 2 }}>
                                {resp.question}
                            </Typography>
                            <Typography style={{ fontSize: '14px', fontWeight: 500, color: theme.palette.text.primary }}>
                                {resp.answer}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            ))}
        </div>
    ) : null;

    // Filter dialog content
    const filterDialogContent = (
        <div className={classes.filterContainer}>
            <div>
                <FormControl variant="outlined" size="small" fullWidth>
                    <InputLabel>Sort By</InputLabel>
                    <Select
                        value={tempFilters.sortBy}
                        onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                        label="Sort By"
                        MenuProps={{
                            PaperProps: {
                                style: {
                                    backgroundColor: theme.palette.background.default,
                                    color: theme.palette.text.primary
                                }
                            }
                        }}
                    >
                        {SORT_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </div>

            <div>
                <FormControl variant="outlined" size="small" fullWidth>
                    <InputLabel>Order By</InputLabel>
                    <Select
                        value={tempFilters.orderBy}
                        onChange={(e) => handleFilterChange('orderBy', e.target.value)}
                        label="Order By"
                        MenuProps={{
                            PaperProps: {
                                style: {
                                    backgroundColor: theme.palette.background.default,
                                    color: theme.palette.text.primary
                                }
                            }
                        }}
                    >
                        {ORDER_BY_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </div>

            <div>
                <FormFieldOmni
                    label="User Reply"
                    name="user_reply"
                    value={tempFilters.user_reply}
                    onChange={(e) => handleFilterChange('user_reply', e.target.value)}
                    placeholder="e.g. Yes, keep me updated"
                    variant="outlined"
                    size="small"
                />
            </div>

            <div>
                <FormFieldOmni
                    label="Customer Number"
                    name="customer_number"
                    value={tempFilters.customer_number}
                    onChange={(e) => handleFilterChange('customer_number', e.target.value)}
                    placeholder="e.g. 923348087124"
                    variant="outlined"
                    size="small"
                />
            </div>
        </div>
    );

    // Filter dialog actions
    const filterDialogActions = (
        <div className={classes.buttonContainer}>
            <div className={classes.buttonGroupModal}>
                <ButtonOmni onClick={handleClearFilters} size="small" variant="outlined" label="Clear" />
            </div>
            <div className={classes.buttonGroupModal}>
                <ButtonOmni onClick={handleCancelFilters} size="small" variant="outlined" label="Cancel" />
                <ButtonOmni onClick={handleApplyFilters} size="small" variant="outlined" label="Apply" />
            </div>
        </div>
    );

    if (!loading && (!data || data.length === 0)) {
        return (
            <>
                <CSVLink ref={csvLink} data={dataforExport} filename={generateFileName()} style={{ display: 'none' }} />
                <Box className={classes.tableSection}>
                    <Box className={classes.headerSection}>
                        <Typography className={classes.tableTitle}>
                            Template Reports
                        </Typography>
                        <Box className={classes.buttonGroup}>
                            {calculatedFilterCount > 0 ? (
                                <Badge badgeContent={calculatedFilterCount} color="primary">
                                    <ButtonOmni
                                        loading={false}
                                        onClick={handleOpenFilterDialog}
                                        size="small"
                                        variant="outlined"
                                        label={
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <FilterListIcon />
                                                Filters
                                            </Box>
                                        }
                                    />
                                </Badge>
                            ) : (
                                <ButtonOmni
                                    loading={false}
                                    onClick={handleOpenFilterDialog}
                                    size="small"
                                    variant="outlined"
                                    label={
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <FilterListIcon />
                                            Filters
                                        </Box>
                                    }
                                />
                            )}
                        </Box>
                    </Box>
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '400px',
                            background: theme.palette.type === 'dark'
                                ? 'rgba(30, 58, 95, 0.6)'
                                : 'rgba(255, 255, 255, 0.7)',
                            borderRadius: '12px',
                            border: `1px solid ${theme.palette.type === 'dark' ? 'rgba(59, 130, 246, 0.3)' : 'rgba(33, 150, 243, 0.3)'}`,
                        }}
                    >
                        <Typography
                            style={{
                                fontSize: '1.3rem',
                                fontWeight: '600',
                                color: theme.palette.text.secondary
                            }}
                        >
                            No Reports Found
                        </Typography>
                    </div>
                </Box>

                <ModalOmni
                    open={filterDialogOpen}
                    handleClose={() => setFilterDialogOpen(false)}
                    title="Table Filters"
                    content={filterDialogContent}
                    actions={filterDialogActions}
                    customWidth={600}
                />

                <ModalOmni
                    open={flowModalOpen}
                    handleClose={() => {
                        setFlowModalOpen(false);
                        setSelectedFlowData(null);
                    }}
                    title="Flow Response Details"
                    content={flowModalContent}
                    customWidth={500}
                />
            </>
        );
    }

    return (
        <>
            <CSVLink ref={csvLink} data={dataforExport} filename={generateFileName()} style={{ display: 'none' }} />
            <Box className={classes.tableSection}>
                <Box className={classes.headerSection}>
                    <Typography className={classes.tableTitle}>
                        Template Reports
                    </Typography>
                    <Box className={classes.buttonGroup}>
                        {calculatedFilterCount > 0 ? (
                            <Badge badgeContent={calculatedFilterCount} color="primary">
                                <ButtonOmni
                                    loading={false}
                                    onClick={handleOpenFilterDialog}
                                    size="small"
                                    variant="outlined"
                                    label={
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <FilterListIcon />
                                            Filters
                                        </Box>
                                    }
                                />
                            </Badge>
                        ) : (
                            <ButtonOmni
                                loading={false}
                                onClick={handleOpenFilterDialog}
                                size="small"
                                variant="outlined"
                                label={
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <FilterListIcon />
                                        Filters
                                    </Box>
                                }
                            />
                        )}
                        {
                            data && data.length && <ButtonOmni
                                loading={isExporting}
                                onClick={handleExport}
                                size="small"
                                variant="outlined"
                                label={
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <GetAppIcon />
                                        {isExporting ? 'Exporting...' : 'Export'}
                                    </Box>
                                }
                            />
                        }

                    </Box>
                </Box>
                <TableOmniNew
                    loading={loading}
                    rows={data || []}
                    headCells={headCells}
                    perPageRows={currentParams.limit}
                    curPage={currentParams.page}
                    setCurrentParams={setCurrentParams}
                    totalItems={totalItems}
                />
            </Box>

            <ModalOmni
                open={filterDialogOpen}
                handleClose={() => setFilterDialogOpen(false)}
                title="Table Filters"
                content={filterDialogContent}
                actions={filterDialogActions}
                customWidth={600}
            />

            <ModalOmni
                open={flowModalOpen}
                handleClose={() => {
                    setFlowModalOpen(false);
                    setSelectedFlowData(null);
                }}
                title="Flow Response Details"
                content={flowModalContent}
                customWidth={500}
            />
        </>
    );
}

export default TemplateReportsTable;